
import { SeverityMatrix, LevelProfile } from "../contracts";

export const SEVERITY_ENGINE_VERSION = "4.0.3-SOFT-ATTACK";

export const BASE_TOLERANCE_60BPM: Record<number, number> = {
    1: 1000, 2: 750, 3: 500, 4: 300, 5: 150, 6: 100, 7: 60, 8: 35
};

export function getTimingToleranceMs(levelId: number, bpm: number): number {
    const base = BASE_TOLERANCE_60BPM[levelId] || 300;
    const ratio = 60 / Math.max(1, bpm);
    return Math.max(15, Math.round(base * ratio));
}

export const SCORING_LOGIC: Record<string, { translate: (m: number, t: number) => string }> = {
    TIMING: {
        translate: (m, t) => m <= t ? "Placement rythmique précis." : "Décalage rythmique détecté."
    },
    HARMONY: {
        translate: (m, t) => m >= t ? "Justesse harmonique validée." : "Défaut de clarté dans les accords."
    },
    TONE: {
        translate: (m, t) => m >= t ? "Timbre riche et équilibré." : "Écart spectral détecté."
    },
    CLEANLINESS: {
        translate: (m, t) => m <= t ? "Jeu net et sans bruits." : "Bruits parasites détectés."
    },
    POSTURE: {
        translate: (m, t) => m >= t ? "Geste stable et optimisé." : "Instabilité gestuelle détectée."
    },
    NUANCE: {
        translate: (m, t) => m >= t ? "Dynamique expressive maîtrisée." : "Manque de contraste dynamique."
    },
    STABILITY: {
        translate: (m, t) => m <= t ? "Flux stable et régulier." : "Fluctuations de pulsation détectées."
    },
    ATTAQUE: {
        translate: (m, t) => m >= t ? "Attaque franche et nette." : "Toucher manquant de relief."
    },
    GENERAL: {
        translate: () => "Analyse globale effectuée."
    }
};

const generateLevel = (id: number, label: string, desc: string, tolerance: number, weightTiming: number, weightHarmony: number): LevelProfile => ({
    label,
    description: desc,
    hardGate: { invalidSignal: true, minSignalDb: -70, maxClipRatio: 0.15 },
    weights: { 
        stability: 15, 
        timing: weightTiming, 
        harmony: weightHarmony, 
        timbre: 10, 
        articulation: 10, 
        nuance: 5, 
        cleanliness: 10, 
        posture: 10 
    },
    thresholds: { master: 0.95, solid: 0.8, ok: 0.5 },
    adviceFocus: id < 4 ? ["stability", "timing"] : ["harmony", "timing", "timbre"],
    timing: { toleranceMsBase: tolerance, allowTrends: id > 3 },
    harmony: { minChordConfidence: 30 + (id * 8) },
    cleanliness: { 
        maxGhostsPerMin: 50 - (id * 5), 
        ghostPenaltyWeight: 0, 
        ghostPenaltyWeightPerGhost: (3 + (id * 1.5)) / 5,
        description: "Clarté du Chant" 
    },
    // PATCH : minTransientRatio recalibré pour la nouvelle sensibilité DSP (Diviseur 5.5)
    articulation: { minTransientRatio: 0.35 + (id * 0.05), description: "Franchise du Geste" },
    dynamics: { minRelativeRange: 0.02 + (id * 0.02) },
    stability: { maxJitterMs: tolerance * 1.5 }
});

export const FACTORY_SEVERITY_MATRIX: SeverityMatrix = {
    1: generateLevel(1, "Découverte", "Initiation au placement.", 1000, 40, 10),
    2: generateLevel(2, "Apprentissage", "Régularité du geste.", 750, 38, 15),
    3: generateLevel(3, "Mise en place", "Stabilité du flux.", 500, 36, 20),
    4: generateLevel(4, "Solidification", "Précision standard.", 300, 34, 25),
    5: generateLevel(5, "Perfectionnement", "Contrôle des nuances.", 150, 32, 30),
    6: generateLevel(6, "Maîtrise", "Justesse spectrale.", 100, 30, 35),
    7: generateLevel(7, "Performance", "Interprétation pro.", 60, 28, 40),
    8: generateLevel(8, "Excellence", "Rigueur de studio.", 35, 25, 45)
};

export let GLOBAL_SEVERITY_MATRIX: SeverityMatrix = { ...FACTORY_SEVERITY_MATRIX };

export function updateSeverityMatrix(newMatrix: SeverityMatrix) {
    GLOBAL_SEVERITY_MATRIX = newMatrix;
}

export function determineRecommendedLevel(metrics: any, bpm: number = 90): { level: number; reason: string; details: string } {
    const timingError = Math.abs(metrics.timingErrorMs || 0);
    const harmonyScore = metrics.harmony || 0;
    const stability = metrics.debug?.stability || 0;
    const cleanliness = metrics.cleanliness || 100;

    if (timingError > 300 || harmonyScore < 40) {
        return { 
            level: 1, 
            reason: "Fondamentaux", 
            details: "Le placement rythmique et la clarté des accords nécessitent une approche 'Découverte' pour consolider les bases." 
        };
    }

    if (timingError > 150 || cleanliness < 60) {
        return { 
            level: 2, 
            reason: "Régularité", 
            details: "La technique est là, mais la régularité (stabilité) doit être travaillée avant d'augmenter l'exigence." 
        };
    }

    if (timingError > 100 || harmonyScore < 70) {
        return { 
            level: 3, 
            reason: "Mise en place", 
            details: "Bon début. Le niveau 3 va vous aider à verrouiller vos transitions d'accords." 
        };
    }

    if (timingError > 60) {
        return { 
            level: 4, 
            reason: "Solidification", 
            details: "C'est le niveau standard. Votre jeu est solide, nous allons maintenant chasser les micro-défauts." 
        };
    }

    if (timingError > 35 || stability < 0.8) {
        return { 
            level: 5, 
            reason: "Perfectionnement", 
            details: "Très bon niveau. Le coach va maintenant être exigeant sur la dynamique et l'attaque." 
        };
    }

    if (timingError > 20) {
        return { 
            level: 6, 
            reason: "Maîtrise", 
            details: "Excellent. On passe en exigence 'Studio' pour polir le grain et le sustain." 
        };
    }

    return { 
        level: 7, 
        reason: "Performance", 
        details: "Niveau Pro détecté. Le coach est calibré pour ne rien laisser passer." 
    };
}
